import React, { useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { DraftFiltersProps, DraftFilter, DraftStatus } from "./types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Search,
  X,
  Calendar,
  User,
  Filter,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";

const statusOptions: {
  value: DraftStatus;
  label: string;
  color: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "draft",
    label: "Draft",
    color: "bg-orange-500",
    icon: <FileText className="w-3 h-3" />,
  },
  {
    value: "pending_review",
    label: "Pending Review",
    color: "bg-blue-500",
    icon: <Clock className="w-3 h-3" />,
  },
  {
    value: "approved",
    label: "Approved",
    color: "bg-green-500",
    icon: <CheckCircle className="w-3 h-3" />,
  },
  {
    value: "rejected",
    label: "Rejected",
    color: "bg-red-500",
    icon: <XCircle className="w-3 h-3" />,
  },
];

export const DraftFilters: React.FC<DraftFiltersProps> = ({
  filter,
  onChange,
  availableAuthors,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [localSearch, setLocalSearch] = useState(filter.searchQuery);

  // Debounced search handler
  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value);

      const timeoutId = setTimeout(() => {
        onChange({
          ...filter,
          searchQuery: value,
        });
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [filter, onChange],
  );

  // Status filter handlers
  const handleStatusToggle = useCallback(
    (status: DraftStatus) => {
      const newStatus = filter.status.includes(status)
        ? filter.status.filter((s) => s !== status)
        : [...filter.status, status];

      onChange({
        ...filter,
        status: newStatus,
      });
    },
    [filter, onChange],
  );

  // Author filter handlers
  const handleAuthorToggle = useCallback(
    (author: string) => {
      const newAuthors = filter.authors.includes(author)
        ? filter.authors.filter((a) => a !== author)
        : [...filter.authors, author];

      onChange({
        ...filter,
        authors: newAuthors,
      });
    },
    [filter, onChange],
  );

  // Date range handlers
  const handleDateRangeChange = useCallback(
    (field: "start" | "end", value: string) => {
      const date = value ? new Date(value) : null;
      const newDateRange = {
        ...filter.dateRange,
        [field]: date,
      };

      onChange({
        ...filter,
        dateRange: newDateRange,
      });
    },
    [filter, onChange],
  );

  // Clear filters
  const handleClearAll = useCallback(() => {
    setLocalSearch("");
    onChange({
      status: [],
      authors: [],
      dateRange: { start: null, end: null },
      searchQuery: "",
    });
  }, [onChange]);

  // Check if any filters are active
  const hasActiveFilters =
    filter.status.length > 0 ||
    filter.authors.length > 0 ||
    filter.dateRange.start !== null ||
    filter.dateRange.end !== null ||
    filter.searchQuery.length > 0;

  // Format date for input
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search drafts by name, description, or author..."
          className="pl-10 pr-10"
          aria-label="Search drafts"
        />
        {localSearch && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleSearchChange("")}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Filter Summary */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
          <div className="flex items-center space-x-2 flex-wrap gap-1">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Active filters:</span>

            {/* Status badges */}
            {filter.status.map((status) => {
              const statusOption = statusOptions.find(
                (opt) => opt.value === status,
              );
              return (
                <Badge key={status} variant="secondary" className="text-xs">
                  {statusOption?.icon}
                  <span className="ml-1">{statusOption?.label}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStatusToggle(status)}
                    className="h-4 w-4 p-0 ml-1"
                    aria-label={`Remove ${statusOption?.label} filter`}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              );
            })}

            {/* Author badges */}
            {filter.authors.map((author) => (
              <Badge key={author} variant="secondary" className="text-xs">
                <User className="w-3 h-3 mr-1" />
                {author}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAuthorToggle(author)}
                  className="h-4 w-4 p-0 ml-1"
                  aria-label={`Remove ${author} filter`}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            className="ml-2"
            aria-label="Clear all filters"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Clear All
          </Button>
        </div>
      )}

      {/* Detailed Filters */}
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-0 h-auto">
            <span className="text-sm font-medium">Filter Options</span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-6 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status Filter */}
            <Card>
              <CardContent className="p-4">
                <Label className="text-sm font-medium mb-3 block flex items-center">
                  <FileText className="w-4 h-4 mr-1" />
                  Status
                </Label>
                <div className="space-y-3">
                  {statusOptions.map(({ value, label, color, icon }) => (
                    <div key={value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${value}`}
                        checked={filter.status.includes(value)}
                        onCheckedChange={() => handleStatusToggle(value)}
                        aria-label={`Filter by ${label} status`}
                      />
                      <label
                        htmlFor={`status-${value}`}
                        className="flex items-center space-x-2 text-sm cursor-pointer flex-1"
                      >
                        <div className="flex items-center space-x-2">
                          <div className={cn("w-2 h-2 rounded-full", color)} />
                          {icon}
                          <span>{label}</span>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Authors Filter */}
            {availableAuthors.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <Label className="text-sm font-medium mb-3 block flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    Authors
                  </Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {availableAuthors.map((author) => (
                      <div key={author} className="flex items-center space-x-2">
                        <Checkbox
                          id={`author-${author}`}
                          checked={filter.authors.includes(author)}
                          onCheckedChange={() => handleAuthorToggle(author)}
                          aria-label={`Filter by author ${author}`}
                        />
                        <label
                          htmlFor={`author-${author}`}
                          className="text-sm cursor-pointer flex-1 truncate"
                          title={author}
                        >
                          {author}
                        </label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Date Range Filter */}
          <Card>
            <CardContent className="p-4">
              <Label className="text-sm font-medium mb-3 block flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Date Range
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="date-start"
                    className="text-xs text-muted-foreground"
                  >
                    From
                  </Label>
                  <Input
                    id="date-start"
                    type="date"
                    value={formatDateForInput(filter.dateRange.start)}
                    onChange={(e) =>
                      handleDateRangeChange("start", e.target.value)
                    }
                    aria-label="Filter from date"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="date-end"
                    className="text-xs text-muted-foreground"
                  >
                    To
                  </Label>
                  <Input
                    id="date-end"
                    type="date"
                    value={formatDateForInput(filter.dateRange.end)}
                    onChange={(e) =>
                      handleDateRangeChange("end", e.target.value)
                    }
                    aria-label="Filter to date"
                  />
                </div>
              </div>
              {(filter.dateRange.start || filter.dateRange.end) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleDateRangeChange("start", "");
                    handleDateRangeChange("end", "");
                  }}
                  className="mt-2 w-full"
                >
                  Clear Date Range
                </Button>
              )}
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default DraftFilters;
